import { useState, useEffect } from 'react'
import { analytics } from '../analytics'

/**
 * Custom hook for video analytics tracking
 * @param {string} videoUrl - The URL of the video being tracked
 * @param {string} videoTitle - The title/description of the video
 * @returns {object} Tracking handlers and state
 */
export function useVideoAnalytics (videoUrl, videoTitle = 'Race Video') {
  const [playedSeconds, setPlayedSeconds] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hasTrackedPlay, setHasTrackedPlay] = useState(false)
  const [progressMilestones] = useState(new Set())

  // Track video play event
  const handlePlay = () => {
    if (!hasTrackedPlay) {
      analytics.track('video_play', {
        content_type: 'video',
        video_title: videoTitle,
        video_url: videoUrl,
        video_current_time: Math.floor(playedSeconds)
      })
      setHasTrackedPlay(true)
    }
  }

  // Track video pause event
  const handlePause = () => {
    analytics.track('video_pause', {
      content_type: 'video',
      video_title: videoTitle,
      video_url: videoUrl,
      video_current_time: Math.floor(playedSeconds),
      video_duration: Math.floor(duration)
    })
  }

  // Track video progress milestones
  const handleProgress = (state) => {
    const currentTime = Math.floor(state.playedSeconds)
    setPlayedSeconds(currentTime)

    // Track progress milestones: 25%, 50%, 75%, 90%, 100%
    const progressPercentages = [25, 50, 75, 90, 100]
    const currentProgress = duration > 0 ? (currentTime / duration) * 100 : 0

    progressPercentages.forEach(percentage => {
      if (currentProgress >= percentage && !progressMilestones.has(percentage)) {
        progressMilestones.add(percentage)
        analytics.track('video_progress', {
          content_type: 'video',
          video_title: videoTitle,
          video_url: videoUrl,
          video_progress: `${percentage}%`,
          video_current_time: currentTime,
          video_duration: Math.floor(duration)
        })
      }
    })
  }

  // Track video complete event
  const handleComplete = () => {
    analytics.track('video_complete', {
      content_type: 'video',
      video_title: videoTitle,
      video_url: videoUrl,
      video_duration_watched: Math.floor(playedSeconds),
      video_duration: Math.floor(duration)
    })
  }

  // Track video duration when ready
  const handleDuration = (videoDuration) => {
    setDuration(videoDuration)
  }

  // Track video seek events
  const handleSeek = (targetTime) => {
    analytics.track('video_seek', {
      content_type: 'video',
      video_title: videoTitle,
      video_url: videoUrl,
      video_current_time: Math.floor(playedSeconds),
      video_seek_time: Math.floor(targetTime),
      video_duration: Math.floor(duration)
    })
  }

  // Reset state when video URL changes
  useEffect(() => {
    setHasTrackedPlay(false)
    progressMilestones.clear()
    setPlayedSeconds(0)
    setDuration(0)
  }, [videoUrl])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setHasTrackedPlay(false)
      progressMilestones.clear()
    }
  }, [])

  return {
    handlePlay,
    handlePause,
    handleProgress,
    handleComplete,
    handleDuration,
    handleSeek,
    playedSeconds,
    duration,
    hasTrackedPlay
  }
}
